import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  FiChevronDown,
  FiHeart,
  FiMenu,
  FiSearch,
  FiShoppingBag,
  FiUser,
  FiX,
} from "react-icons/fi";
import productData from "../Datas/productData";
import siteConfig from "../config/siteConfig";
import { CartContext } from "./CartContext";
import "../css/header.scss";

function Header() {
  const { cartItems, wishlistItems = [], removeFromWishlist } =
    useContext(CartContext);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [wishlistOpen, setWishlistOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const wishlistProducts = useMemo(
    () => productData.filter((product) => wishlistItems.includes(product.id)),
    [wishlistItems]
  );

  const cartProducts = useMemo(
    () =>
      cartItems
        .map((item) => ({
          ...item,
          product: productData.find((product) => product.id === item.id),
        }))
        .filter((item) => item.product),
    [cartItems]
  );

  const cartCount = cartProducts.reduce(
    (total, item) => total + Number(item.quantity || 0),
    0
  );
  const wishlistCount = wishlistProducts.length;

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setWishlistOpen(false);
    setCartOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const hasOverlay = mobileOpen || searchOpen || wishlistOpen || cartOpen;
    document.body.classList.toggle("overlay-open", hasOverlay);
    return () => document.body.classList.remove("overlay-open");
  }, [mobileOpen, searchOpen, wishlistOpen, cartOpen]);

  const closePanels = () => {
    setMobileOpen(false);
    setSearchOpen(false);
    setWishlistOpen(false);
    setCartOpen(false);
  };

  const openPanel = (panel) => {
    closePanels();
    if (panel === "search") setSearchOpen(true);
    if (panel === "wishlist") setWishlistOpen(true);
    if (panel === "cart") setCartOpen(true);
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const term = searchTerm.trim();
    navigate(term ? `/shop?search=${encodeURIComponent(term)}` : "/shop");
  };

  const money = (value) =>
    new Intl.NumberFormat(siteConfig.currency?.locale || "en-US", {
      style: "currency",
      currency: siteConfig.currency?.code || "USD",
    }).format(Number(value));

  return (
    <>
      <header className="site-header">
        <div className="announcement-bar">
          <p>
            TRACKED SHIPPING {money(siteConfig.shipping?.standardRate ?? 9.99)}
          </p>
          <span>Designed for intentional days</span>
        </div>

        <div className="header-utility" aria-label="Utility navigation">
          <Link to="/about">OUR STORY</Link>
          <button type="button" onClick={() => openPanel("wishlist")}>
            WISHLIST ({wishlistCount})
          </button>
          <Link to="/contact">CONCIERGE</Link>
          <span>
            {siteConfig.currency?.code || "USD"} {siteConfig.currency?.symbol || "$"}
          </span>
        </div>

        <div className="header-main">
          <button
            type="button"
            className="header-icon mobile-menu-trigger"
            onClick={() => {
              closePanels();
              setMobileOpen(true);
            }}
            aria-label="Open navigation"
          >
            <FiMenu />
          </button>

          <nav className="primary-nav primary-nav--left" aria-label="Primary">
            <NavLink to="/shop?collection=new">NEW</NavLink>
            <div className="nav-dropdown">
              <NavLink to="/shop" className="nav-dropdown__trigger">
                SHOP <FiChevronDown />
              </NavLink>
              <div className="mega-menu">
                <div>
                  <p>SHOP BY CATEGORY</p>
                  <Link to="/shop?category=Planners">Planner Systems</Link>
                  <Link to="/shop?category=Planner+Inserts">Planner Inserts</Link>
                  <Link to="/shop?category=Notebooks">Notebooks</Link>
                  <Link to="/shop?category=Writing+Tools">Writing Tools</Link>
                  <Link to="/shop?category=Storage+%26+Desk">Desk & Storage</Link>
                </div>
                <div>
                  <p>START PLANNING</p>
                  <Link to="/shop?size=A5">A5 Systems</Link>
                  <Link to="/shop?size=Personal">Personal Systems</Link>
                  <Link to="/shop?tag=best-seller">Best Sellers</Link>
                  <Link to="/shop?collection=new">New Arrivals</Link>
                  <Link to="/shop">Shop All</Link>
                </div>
                <Link className="mega-menu__feature" to="/shop?tag=planner-system">
                  <img
                    src="/img/generated/header-planner-foundation.png"
                    alt="A curated modular planner collection"
                  />
                  <span>BUILD YOUR PLANNING FOUNDATION →</span>
                </Link>
              </div>
            </div>
            <NavLink to="/shop?tag=planner-system">GET STARTED</NavLink>
          </nav>

          <Link className="brand-wordmark" to="/" aria-label={siteConfig.logo?.alt || siteConfig.brandName}>
            <span className="brand-mark" aria-hidden="true"><i>C</i><i>M</i></span>
            <span className="brand-lettering" aria-hidden="true"><b>CEO</b><em>WAND</em><b>MILL</b></span>
          </Link>

          <nav className="primary-nav primary-nav--right" aria-label="Explore">
            <NavLink to="/shop?category=Gift+Sets">GIFT</NavLink>
            <NavLink to="/about">LEARN</NavLink>
            <NavLink to="/shop?tag=sale">SALE</NavLink>
            <div className="nav-dropdown nav-more">
              <button type="button" className="nav-dropdown__trigger">MORE <FiChevronDown /></button>
              <div className="more-menu">
                <Link to="/privacy">Privacy Policy</Link>
                <Link to="/cookies">Cookie Policy</Link>
                <Link to="/faqs">FAQs</Link>
                <Link to="/terms">Terms &amp; Conditions</Link>
                <Link to="/shipping">Shipping</Link>
                <Link to="/returns">Returns</Link>
              </div>
            </div>
          </nav>

          <div className="header-actions">
            <button
              type="button"
              className="header-icon"
              onClick={() => openPanel("search")}
              aria-label="Search"
            >
              <FiSearch />
            </button>
            <Link className="header-icon header-account" to="/contact" aria-label="Account">
              <FiUser />
            </Link>
            <button
              type="button"
              className="header-icon header-wishlist"
              onClick={() => openPanel("wishlist")}
              aria-label={`Wishlist with ${wishlistCount} items`}
            >
              <FiHeart />
              {wishlistCount > 0 && <span>{wishlistCount}</span>}
            </button>
            <button
              type="button"
              className="header-icon"
              onClick={() => openPanel("cart")}
              aria-label={`Cart with ${cartCount} items`}
            >
              <FiShoppingBag />
              {cartCount > 0 && <span>{cartCount}</span>}
            </button>
          </div>
        </div>
      </header>

      <div
        className={`header-backdrop ${
          mobileOpen || searchOpen || wishlistOpen || cartOpen ? "is-open" : ""
        }`}
        onClick={closePanels}
        aria-hidden="true"
      />

      <aside className={`mobile-drawer ${mobileOpen ? "is-open" : ""}`}>
        <div className="drawer-heading">
          <span>MENU</span>
          <button type="button" onClick={closePanels} aria-label="Close navigation">
            <FiX />
          </button>
        </div>
        <nav aria-label="Mobile navigation">
          <Link to="/shop?collection=new">NEW ARRIVALS</Link>
          <Link to="/shop">SHOP ALL</Link>
          <Link to="/shop?category=Planners">PLANNER SYSTEMS</Link>
          <Link to="/shop?category=Planner+Inserts">INSERTS & REFILLS</Link>
          <Link to="/shop?category=Notebooks">NOTEBOOKS</Link>
          <Link to="/shop?category=Storage+%26+Desk">DESK & STORAGE</Link>
          <Link to="/shop?category=Writing+Tools">WRITING TOOLS</Link>
          <Link to="/shop?category=Gift+Sets">GIFT SETS</Link>
          <Link to="/about">OUR STORY</Link>
          <Link to="/contact">CONTACT</Link>
          <Link to="/privacy">PRIVACY POLICY</Link>
          <Link to="/cookies">COOKIE POLICY</Link>
          <Link to="/faqs">FAQS</Link>
        </nav>
      </aside>

      <aside className={`search-panel ${searchOpen ? "is-open" : ""}`}>
        <div className="drawer-heading">
          <span>SEARCH</span>
          <button type="button" onClick={closePanels} aria-label="Close search">
            <FiX />
          </button>
        </div>
        <form onSubmit={handleSearch}>
          <label htmlFor="site-search">What are you looking for?</label>
          <div>
            <input
              id="site-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="Search planners, inserts, accessories..."
              autoFocus={searchOpen}
            />
            <button type="submit" aria-label="Submit search">
              <FiSearch />
            </button>
          </div>
        </form>
        <p>POPULAR SEARCHES</p>
        <div className="search-suggestions">
          <Link to="/shop?search=agenda">Leather agendas</Link>
          <Link to="/shop?search=insert">Planner inserts</Link>
          <Link to="/shop?search=notebook">Notebooks</Link>
        </div>
      </aside>

      <aside className={`side-drawer ${wishlistOpen ? "is-open" : ""}`}>
        <div className="drawer-heading">
          <span>YOUR WISHLIST ({wishlistProducts.length})</span>
          <button type="button" onClick={closePanels} aria-label="Close wishlist">
            <FiX />
          </button>
        </div>
        <div className="drawer-content">
          {wishlistProducts.length === 0 ? (
            <div className="drawer-empty">
              <FiHeart />
              <h2>Your list is waiting.</h2>
              <p>Save considered pieces and return to them whenever inspiration strikes.</p>
              <Link className="button button--dark" to="/shop">
                DISCOVER THE COLLECTION
              </Link>
            </div>
          ) : (
            <>
              <div className="drawer-items">
                {wishlistProducts.map((product) => (
                  <article key={product.id} className="drawer-item">
                    <Link to={`/shopDetails/${product.id}`}>
                      <img src={product.image} alt={product.name} />
                    </Link>
                    <div>
                      <Link to={`/shopDetails/${product.id}`}>{product.name}</Link>
                      <span>{money(product.price)}</span>
                      <button
                        type="button"
                        onClick={() => removeFromWishlist(product.id)}
                      >
                        REMOVE
                      </button>
                    </div>
                  </article>
                ))}
              </div>
              <Link className="button button--dark drawer-cta" to="/shop">
                CONTINUE SHOPPING
              </Link>
            </>
          )}
        </div>
      </aside>

      <aside className={`side-drawer ${cartOpen ? "is-open" : ""}`}>
        <div className="drawer-heading">
          <span>YOUR CART ({cartCount})</span>
          <button type="button" onClick={closePanels} aria-label="Close cart">
            <FiX />
          </button>
        </div>
        <div className="drawer-content">
          {cartProducts.length === 0 ? (
            <div className="drawer-empty">
              <FiShoppingBag />
              <h2>Your cart is empty.</h2>
              <p>Build a planning system that brings clarity to every day.</p>
              <Link className="button button--dark" to="/shop">
                CONTINUE BROWSING
              </Link>
            </div>
          ) : (
            <>
              <div className="drawer-items">
                {cartProducts.map((item) => (
                  <article
                    key={`${item.id}-${item.size}-${item.color}`}
                    className="drawer-item"
                  >
                    <Link to={`/shopDetails/${item.product.id}`}>
                      <img src={item.product.image} alt={item.product.name} />
                    </Link>
                    <div>
                      <Link to={`/shopDetails/${item.product.id}`}>
                        {item.product.name}
                      </Link>
                      <span>
                        {item.quantity} × {money(item.product.price)}
                      </span>
                      <small>{[item.size, item.color].filter(Boolean).join(" · ")}</small>
                    </div>
                  </article>
                ))}
              </div>
              <Link className="button button--dark drawer-cta" to="/cartpage">
                VIEW CART
              </Link>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

export default Header;
